'use client';

import { LIST_VIEW, WEEK_VIEW } from '@/components/constants';
import { TimedEntryDashboardViewType } from '@/components/types';
import { EventResponse } from '@/modules/Services/v3';
import { ErrorManagerContextProvider } from '@/providers/ErrorManagerContext';
import { DashboardPermissions } from '@/types';
import { DashboardFeatureFlags } from '@/utils/featureFlags';
import { RealityWarperContextProvider } from '@/utils/tools/RealityWarper';
import { Stage } from '@eventbrite/webapp-constants';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

interface DashboardOptions {
  stage: Stage;
  event?: EventResponse;
  permissions?: DashboardPermissions;
  // Default view functionality
  defaultView: TimedEntryDashboardViewType;
  setDefaultView: React.Dispatch<React.SetStateAction<TimedEntryDashboardViewType>>;
  setEvent: React.Dispatch<React.SetStateAction<EventResponse | undefined>>;
}

interface DashboardProviderProps {
  children: ReactNode;
  permissions?: DashboardPermissions;
  stage: Stage;
  event?: EventResponse;
  featureFlags: DashboardFeatureFlags;
}

const DashboardContext = createContext<DashboardOptions | undefined>(undefined);

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  permissions,
  stage,
  featureFlags,
}) => {
  // Helper function to validate and ensure the view type is valid
  const validateViewType = (view: string | null): TimedEntryDashboardViewType => {
    return view === WEEK_VIEW ? WEEK_VIEW : LIST_VIEW;
  };

  // FIX: Always initialize with a consistent default (LIST_VIEW) to prevent hydration mismatch.
  // Server and client will both render LIST_VIEW on first paint.
  const [defaultView, setDefaultView] = useState<TimedEntryDashboardViewType>(LIST_VIEW);

  // FIX: Sync with localStorage AFTER hydration is complete to avoid mismatch.
  // This runs only on the client after the initial render.
  useEffect(() => {
    const storedView = window.localStorage.getItem('defaultToggle');
    const validatedView = validateViewType(storedView);
    if (validatedView !== LIST_VIEW) {
      setDefaultView(validatedView);
    }
  }, []);

  // Update localStorage whenever the view changes
  const updateDefaultView = (newView: React.SetStateAction<TimedEntryDashboardViewType>) => {
    setDefaultView((prevView: TimedEntryDashboardViewType) => {
      const resolvedView = typeof newView === 'function' ? newView(prevView) : newView;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('defaultToggle', resolvedView);
      }
      return resolvedView;
    });
  };

  const [event, setEvent] = useState<EventResponse | undefined>();

  const dashboardOptions = useMemo(
    () => ({
      stage,
      permissions,
      defaultView,
      setDefaultView: updateDefaultView,
      event,
      setEvent,
    }),
    [stage, defaultView, event, permissions],
  );

  return (
    <DashboardContext.Provider value={dashboardOptions}>
      <RealityWarperContextProvider featureFlags={featureFlags}>
        <ErrorManagerContextProvider>{children}</ErrorManagerContextProvider>
      </RealityWarperContextProvider>
    </DashboardContext.Provider>
  );
};

/**
 * Hook to access dashboard context values
 *
 * @returns Object containing:
 * - `stage`: Current deployment stage
 * - `defaultView`: Current view type (LIST_VIEW | WEEK_VIEW)
 * - `setDefaultView`: Function to update the view type
 *
 * @throws Error if used outside of DashboardProvider
 *
 * @example
 * ```tsx
 * function DashboardComponent() {
 *   const { stage, defaultView, setDefaultView } = useDashboard();
 *
 *   return (
 *     <div>
 *       <p>Stage: {stage}</p>
 *       <p>View: {defaultView}</p>
 *       <button onClick={() => setDefaultView(defaultView === LIST_VIEW ? WEEK_VIEW : LIST_VIEW)}>
 *         Toggle View
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useDashboard = (): DashboardOptions => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

/**
 * Hook to access and manage the default view setting
 *
 * This is a convenience hook that extracts only the view-related functionality
 * from the dashboard context, making it easier to use in components that only
 * need view management.
 *
 * @returns Object containing:
 * - `defaultView`: Current view type (LIST_VIEW | WEEK_VIEW)
 * - `setDefaultView`: Function to update the view type
 *
 * @throws Error if used outside of DashboardProvider
 *
 * @example
 * ```tsx
 * function ViewToggle() {
 *   const { defaultView, setDefaultView } = useDefaultView();
 *
 *   return (
 *     <button onClick={() => setDefaultView(defaultView === LIST_VIEW ? WEEK_VIEW : LIST_VIEW)}>
 *       Switch to {defaultView === LIST_VIEW ? 'Week' : 'List'} View
 *     </button>
 *   );
 * }
 * ```
 */
export const useDefaultView = () => {
  const { defaultView, setDefaultView } = useDashboard();
  return { defaultView, setDefaultView };
};
